
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import LodgingForm, { createEmptyLodgingDraft } from '../components/LodgingForm';
import PageActions from '../components/PageActions';
import 'leaflet/dist/leaflet.css';
import TripMap from '../components/TripMap';
import {
  buildLegsPayload,
  deriveStartEnd,
  hydrateLegDraftsFromActivity,
  cityKeyFromText,
  openNativePicker,
  toMinutes,
} from '../utils/travelLegs';
import { formatOvernightArrivalDescription } from '../utils/activityDescriptions';
import { buildGreatCirclePolyline, DEFAULT_ROUTE_PATH_OPTIONS, DEFAULT_CONNECTOR_PATH_OPTIONS, DEFAULT_WALK_PATH_OPTIONS } from '../utils/mapArcs';
import { buildTripRouteStops } from '../utils/tripRoute';
import { buildVisualForType } from '../utils/activityVisuals';
import { markerIconDataUrl } from '../components/icons/ActivityIcon';
import ItineraryDayView from './ItineraryDayView';
import TravelOverview from '../components/TravelOverview';
import TripCalendar from '../components/TripCalendar';
import { formatTime24, formatDateLocal, formatDateReadable, getDatesForDestination } from '../utils/dateTime';
import { formatAddressSummary, buildGoogleMapsUrl } from '../utils/formatters';
import { getApiBaseUrl } from '../services/api';
import FlightImportModal from '../components/FlightImportModal';
import ActivityModal from '../components/ActivityModal';
import LodgingImagesModal from '../components/LodgingImagesModal';
import UnescoSuggestionBanner from '../components/UnescoSuggestionBanner';
import { apiUrl } from '../services/api';
import ActivityTableRow from '../components/ActivityTableRow';
import { sortByDate } from '../utils/sort';
import { buildLodgingEventsByDateDest } from '../utils/lodgingEvents';
import { requireFields, validateDateRange } from '../utils/validation';
import { buildIsTravelType, buildIsTravelCategory } from '../utils/activityTypes';
import { useMenuClose } from '../hooks/useMenuClose';

const VIEW_MODES = ['destination', 'day', 'overview', 'travel', 'calendar'];
const VIEW_MODE_LABELS = { destination: 'Destination', day: 'Day', overview: 'Overview', travel: 'Travel', calendar: 'Calendar' };

function toAbsoluteImageUrl(url) {
  const u = String(url || '').trim();
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  if (u.startsWith('//')) return `https:${u}`;
  const base = getApiBaseUrl();
  return u.startsWith('/') ? `${base}${u}` : `${base}/${u}`;
}

function Itinerary() {
  // =========================
  // State: trip context
  // =========================
  const { tripId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [viewMode, setViewMode] = useState(() => {
    const fromUrl = searchParams.get('view');
    if (VIEW_MODES.includes(fromUrl)) return fromUrl;
    try {
      const fromStorage = localStorage.getItem(`itinerary-view-${tripId}`);
      if (VIEW_MODES.includes(fromStorage)) return fromStorage;
    } catch (_) { /* ignore */ }
    return 'destination';
  });

  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('view', viewMode);
      return next;
    }, { replace: true });
    try {
      localStorage.setItem(`itinerary-view-${tripId}`, viewMode);
    } catch (_) { /* ignore */ }
  }, [viewMode, tripId, setSearchParams]);

  useEffect(() => {
    const urlView = searchParams.get('view');
    if (VIEW_MODES.includes(urlView)) {
      setViewMode((m) => (m !== urlView ? urlView : m));
    }
  }, [searchParams]);

  const [tripName, setTripName] = useState('');
  const [tripCompleted, setTripCompleted] = useState(false);
  const [isSavingTripCompleted, setIsSavingTripCompleted] = useState(false);

  // =========================
  // State: core data lists
  // =========================
  const [destinations, setDestinations] = useState([]);
  const [activities, setActivities] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [activityCategories, setActivityCategories] = useState([]);

  // =========================
  // State: add/edit drafts
  // =========================
  const [newActivity, setNewActivity] = useState({}); // keyed by date+destination
  const [newDestination, setNewDestination] = useState({ destination_name: '', start_date: '', end_date: '' });
  const [newDestinationLodging, setNewDestinationLodging] = useState(createEmptyLodgingDraft());
  const [isAddingDestination, setIsAddingDestination] = useState(false);
  const [isAddingDestinationLodging, setIsAddingDestinationLodging] = useState(false);
  const [destinationSuggestions, setDestinationSuggestions] = useState([]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [editingByActivityId, setEditingByActivityId] = useState({});

  // =========================
  // Map: route points + derived polylines
  // =========================
  const [routePoints, setRoutePoints] = useState([]);

  const fetchRoutePointsForTrip = useCallback(() => {
    fetch(apiUrl('/api/destinations/points'))
      .then(res => res.ok ? res.json() : [])
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setRoutePoints(list.filter(p => String(p.trip_id) === String(tripId)));
      })
      .catch(() => setRoutePoints([]));
  }, [tripId]);

  const [openActivityMenuId, setOpenActivityMenuId] = useState(null);
  const [openAddActivityKey, setOpenAddActivityKey] = useState(null);
  const [openBulkMenuKey, setOpenBulkMenuKey] = useState(null);
  const [selectedActivityIdsByKey, setSelectedActivityIdsByKey] = useState({});

  const [openDestinationMenuId, setOpenDestinationMenuId] = useState(null);

  const [openLodgingMenuId, setOpenLodgingMenuId] = useState(null);
  const [managingPhotosLodging, setManagingPhotosLodging] = useState(null);
  const [unescoSuggestion, setUnescoSuggestion] = useState(null); // { activityId, ideaId, suggestion }
  const [editingLodgingId, setEditingLodgingId] = useState(null);
  const [editLodging, setEditLodging] = useState(createEmptyLodgingDraft());
  const [lodgingPreviewByUrl, setLodgingPreviewByUrl] = useState({});
  const [, setIsUploadingLodgingId] = useState(null);
  const [editLodgingFiles, setEditLodgingFiles] = useState([]);
  const [lodgingCarouselIndex, setLodgingCarouselIndex] = useState({});

  const [sunEvents, setSunEvents] = useState([]);
  const [showFlightImport, setShowFlightImport] = useState(false);

  const [lodgings, setLodgings] = useState([]);
  const [newLodgingByDestinationId, setNewLodgingByDestinationId] = useState({});
  const [newLodgingFilesByDestinationId, setNewLodgingFilesByDestinationId] = useState({});
  const [isAddingLodgingByDestinationId, setIsAddingLodgingByDestinationId] = useState({});

  const [editingDestinationId, setEditingDestinationId] = useState(null);
  const [editDestination, setEditDestination] = useState({ destination_name: '', start_date: '', end_date: '' });
  const [editDestinationSuggestions, setEditDestinationSuggestions] = useState([]);
  const [editActiveSuggestionIndex, setEditActiveSuggestionIndex] = useState(-1);
  const [editSelectedPlace, setEditSelectedPlace] = useState(null);

  const [collapsedDestinationIds, setCollapsedDestinationIds] = useState({});

  // =========================
  // Derived data: ordering + grouping
  // =========================
  const sortedDestinations = useMemo(() => sortByDate(destinations, 'start_date'), [destinations]);

  const [mapVisible, setMapVisible] = useState(true);
  const [currentDestinationId, setCurrentDestinationId] = useState(null);
  const destinationSectionRefs = useRef({});
  const destinationTabRefs = useRef({});

  const destinationsToShow = useMemo(
    () =>
      viewMode === 'destination' && currentDestinationId
        ? sortedDestinations.filter((d) => String(d.destination_id) === String(currentDestinationId))
        : sortedDestinations,
    [viewMode, currentDestinationId, sortedDestinations]
  );
  const forceExpanded = viewMode === 'destination';

  useEffect(() => {
    const q = (newDestination.destination_name || '').trim();
    if (q.length < 2) {
      setDestinationSuggestions([]);
      setActiveSuggestionIndex(-1);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(() => {
      fetch(apiUrl(`/api/places/suggest?q=${encodeURIComponent(q)}&limit=6`), {
        signal: controller.signal
      })
        .then(res => res.ok ? res.json() : [])
        .then(data => {
          if (Array.isArray(data)) {
            setDestinationSuggestions(data);
            setActiveSuggestionIndex(-1);
          }
        })
        .catch(() => { /* ignore */ });
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [newDestination.destination_name]);

  useEffect(() => {
    if (!editingDestinationId) return;
    const q = (editDestination.destination_name || '').trim();
    if (q.length < 2) {
      setEditDestinationSuggestions([]);
      setEditActiveSuggestionIndex(-1);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(() => {
      fetch(apiUrl(`/api/places/suggest?q=${encodeURIComponent(q)}&limit=6`), {
        signal: controller.signal
      })
        .then(res => res.ok ? res.json() : [])
        .then(data => {
          if (Array.isArray(data)) {
            setEditDestinationSuggestions(data);
            setEditActiveSuggestionIndex(-1);
          }
        })
        .catch(() => { /* ignore */ });
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [editingDestinationId, editDestination.destination_name]);

  const selectSuggestion = (s) => {
    setNewDestination(prev => ({ ...prev, destination_name: s.label || s.display_name || prev.destination_name }));
    setSelectedPlace(s);
    setDestinationSuggestions([]);
    setActiveSuggestionIndex(-1);
  };

  const selectEditSuggestion = (s) => {
    setEditDestination(prev => ({ ...prev, destination_name: s.label || s.display_name || prev.destination_name }));
    setEditSelectedPlace(s);
    setEditDestinationSuggestions([]);
    setEditActiveSuggestionIndex(-1);
  };

  useEffect(() => {
    fetch(apiUrl('/api/trips'))
      .then(res => res.json())
      .then(trips => {
        const trip = trips.find(t => t.trip_id === tripId);
        if (trip) {
          setTripName(trip.trip_name);
          setTripCompleted(Boolean(trip.completed));
        }
      });
    fetch(apiUrl(`/api/trips/${tripId}/destinations`))
      .then(res => res.json())
      .then(setDestinations);
    fetch(apiUrl(`/api/trips/${tripId}/activities`))
      .then(res => res.json())
      .then(setActivities);

    fetch(apiUrl(`/api/trips/${tripId}/sun_events`))
      .then(res => res.ok ? res.json() : [])
      .then(setSunEvents)
      .catch(() => setSunEvents([]));

    fetch(apiUrl(`/api/trips/${tripId}/lodgings`))
      .then(res => res.ok ? res.json() : [])
      .then(setLodgings)
      .catch(() => setLodgings([]));

    fetchRoutePointsForTrip();
  }, [tripId, fetchRoutePointsForTrip]);

  useEffect(() => {
    const list = Array.isArray(lodgings) ? lodgings : [];
    const urls = Array.from(new Set(list.map(l => String(l?.booking_url || '').trim()).filter(Boolean)));
    if (!urls.length) return;

    let cancelled = false;
    const missing = urls.filter(u => lodgingPreviewByUrl[u] == null);
    if (!missing.length) return;

    (async () => {
      for (const url of missing) {
        try {
          const res = await fetch(apiUrl(`/api/link_preview?url=${encodeURIComponent(url)}`));
          const payload = await res.json().catch(() => ({}));
          if (cancelled) return;
          setLodgingPreviewByUrl(prev => ({ ...prev, [url]: payload?.ok ? payload : { ok: true, url, title: '', image: '' } }));
        } catch (e) {
          if (cancelled) return;
          setLodgingPreviewByUrl(prev => ({ ...prev, [url]: { ok: true, url, title: '', image: '' } }));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [lodgings, lodgingPreviewByUrl]);

  const uploadLodgingImage = async (lodgingId, file) => {
    if (!lodgingId || !file) return null;
    setIsUploadingLodgingId(lodgingId);
    try {
      const body = new FormData();
      body.append('file', file);
      const res = await fetch(apiUrl(`/api/trips/${tripId}/lodgings/${lodgingId}/image`), {
        method: 'POST',
        body,
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        window.alert(payload.error || 'Failed to upload lodging image');
        return null;
      }
      if (payload?.image_url) {
        setLodgings(prev => (
          Array.isArray(prev)
            ? prev.map(l => (String(l?.lodging_id) === String(lodgingId) ? { ...l, booking_image_url: payload.image_url } : l))
            : prev
        ));
      }
      return payload?.image_url || null;
    } catch (e) {
      window.alert('Failed to upload lodging image');
      return null;
    } finally {
      setIsUploadingLodgingId(null);
    }
  };

  const refetchTypes = useCallback(() => {
    fetch(apiUrl('/api/activity_types'))
      .then(res => res.ok ? res.json() : [])
      .then(setActivityTypes)
      .catch(() => setActivityTypes([]));
  }, []);

  useEffect(() => {
    refetchTypes();
    fetch(apiUrl('/api/activity_categories'))
      .then(res => res.ok ? res.json() : [])
      .then(setActivityCategories)
      .catch(() => setActivityCategories([]));
  }, [refetchTypes]);

  const anyMenuOpen = !!(openActivityMenuId || openAddActivityKey || openBulkMenuKey || openLodgingMenuId || openDestinationMenuId);
  useMenuClose(anyMenuOpen, () => {
    setOpenActivityMenuId(null);
    setOpenAddActivityKey(null);
    setOpenBulkMenuKey(null);
    setOpenLodgingMenuId(null);
    setOpenDestinationMenuId(null);
  });

  // =========================
  // Derived helpers: travel detection
  // =========================
  const isTravelTypeId = useMemo(
    () => buildIsTravelType(activityTypes, activityCategories),
    [activityTypes, activityCategories]
  );

  const isTravelCategoryId = useMemo(
    () => buildIsTravelCategory(activityCategories),
    [activityCategories]
  );

  // Resolve an activity's type_id -> its category's Lucide icon + color (0017).
  const visualForType = useMemo(
    () => buildVisualForType(activityTypes, activityCategories),
    [activityTypes, activityCategories]
  );

  // =========================
  // Derived: route stops and arcs
  // =========================
  const { displayRouteStops, routeCoords, destinationCoords, destinationCountryCode, destinationSegments, travelSegments } = useMemo(() => {
    const result = buildTripRouteStops({
      sortedDestinations,
      routePoints,
      activities,
      isTravelTypeId,
      mode: 'auto',
    });
    return {
      displayRouteStops: result.stops,
      routeCoords: result.coords,
      destinationCoords: result.destinationCoords || [],
      destinationCountryCode: result.destinationCountryCode || '',
      destinationSegments: result.destinationSegments || [],
      travelSegments: result.travelSegments || [],
    };
  }, [sortedDestinations, routePoints, activities, isTravelTypeId]);

  const singleStopCountryCode = useMemo(() => {
    if (!Array.isArray(displayRouteStops) || displayRouteStops.length !== 1) return '';
    const stop = displayRouteStops[0];
    const id = stop?.destination_id;
    if (!id || id === 'from' || id === 'to') return '';
    const d = (Array.isArray(destinations) ? destinations : []).find(x => String(x?.destination_id) === String(id));
    return String(d?.country_code || '').toUpperCase();
  }, [destinations, displayRouteStops]);

  const mapFitPoints = destinationCoords.length > 0 ? destinationCoords : routeCoords;
  const mapCountryCode = mapFitPoints.length === 1 && destinationCoords.length === 1 ? destinationCountryCode : singleStopCountryCode;

  const effectiveMapFitPoints = useMemo(() => {
    if (viewMode !== 'destination' || !currentDestinationId || !routePoints.length) return mapFitPoints;
    const pt = (Array.isArray(routePoints) ? routePoints : []).find(
      (p) => p && String(p?.destination_id) === String(currentDestinationId)
    );
    if (!pt || !Number.isFinite(Number(pt.lat)) || !Number.isFinite(Number(pt.lon))) return mapFitPoints;
    return [[Number(pt.lat), Number(pt.lon)]];
  }, [viewMode, currentDestinationId, routePoints, mapFitPoints]);

  const isDestinationCityView = effectiveMapFitPoints.length === 1 && viewMode === 'destination';
  const mapFitMaxZoom = isDestinationCityView ? 12 : 5;

  // Per-activity points (activities carrying their own coordinates), grouped by
  // destination + day and ordered by time. Feed both subtle map markers and the
  // activity-to-activity routed paths below.
  const activityMapPoints = useMemo(() => {
    // Valid point = both finite AND not the (0,0) null-island sentinel (a zeroed/
    // missing coordinate that would otherwise plot in the Gulf of Guinea).
    const isValidPt = (la, lo) =>
      Number.isFinite(la) && Number.isFinite(lo) && !(Math.abs(la) < 1e-6 && Math.abs(lo) < 1e-6);
    const pts = [];
    (Array.isArray(activities) ? activities : []).forEach((a) => {
      if (!a || !a.destination_id) return;
      const fromLat = Number(a.from_lat);
      const fromLon = Number(a.from_lon);
      const toLat = Number(a.to_lat);
      const toLon = Number(a.to_lon);
      // Skip real travel (drawn as legs/arcs elsewhere): travel-typed, multi-leg,
      // or moving between two valid endpoints.
      const isTravel =
        isTravelTypeId(a.type_id) ||
        (Array.isArray(a.legs) && a.legs.length > 0) ||
        (isValidPt(fromLat, fromLon) && isValidPt(toLat, toLon));
      if (isTravel) return;
      // The activity's point: prefer its own location_*, fall back to from_*
      // (older activities stored their single location there).
      let lat = Number(a.location_lat);
      let lon = Number(a.location_lon);
      if (!isValidPt(lat, lon)) { lat = fromLat; lon = fromLon; }
      if (!isValidPt(lat, lon)) return;
      const vis = visualForType(a.type_id);
      pts.push({
        destination_id: String(a.destination_id),
        day: String(a.start_time || '').slice(0, 10),
        t: String(a.start_time || ''),
        sort: Number(a.sort_order) || 0,
        lat,
        lon,
        title: a.title || '',
        address: String(a.location_address || a.from_address || '').trim(),
        iconUrl: markerIconDataUrl(vis.categoryName),
        color: vis.color,
      });
    });
    return pts;
  }, [activities, isTravelTypeId, visualForType]);

  // Activity-to-activity segments: consecutive activities within the same
  // destination & day, in time order. Rendered as Google Directions (walking)
  // with a straight-line fallback.
  const activitySegments = useMemo(() => {
    const groups = {};
    activityMapPoints.forEach((p) => {
      const key = `${p.destination_id}|${p.day}`;
      (groups[key] || (groups[key] = [])).push(p);
    });
    const segs = [];
    Object.values(groups).forEach((list) => {
      list.sort((x, y) => (x.t === y.t ? x.sort - y.sort : String(x.t).localeCompare(String(y.t))));
      for (let i = 0; i < list.length - 1; i++) {
        segs.push([[list[i].lat, list[i].lon], [list[i + 1].lat, list[i + 1].lon]]);
      }
    });
    return segs;
  }, [activityMapPoints]);

  const mapPolylines = useMemo(() => {
    const lines = [];
    destinationSegments.forEach(([from, to]) => {
      // City-to-city ground connectors: ask the renderer to follow real roads
      // (Google Directions) when it can, falling back to this straight line.
      lines.push({ positions: [from, to], pathOptions: DEFAULT_CONNECTOR_PATH_OPTIONS, routable: true, travelMode: 'DRIVING' });
    });
    travelSegments.forEach(([from, to]) => {
      const pts = buildGreatCirclePolyline(from, to);
      if (pts.length) lines.push({ positions: pts, pathOptions: DEFAULT_ROUTE_PATH_OPTIONS });
    });
    // Walking routes between consecutive activities within a day.
    activitySegments.forEach(([from, to]) => {
      lines.push({ positions: [from, to], pathOptions: DEFAULT_WALK_PATH_OPTIONS, routable: true, travelMode: 'WALKING' });
    });
    return lines;
  }, [destinationSegments, travelSegments, activitySegments]);

  const destinationTzById = useMemo(() => {
    const m = {};
    (Array.isArray(destinations) ? destinations : []).forEach(d => {
      if (d && d.destination_id) m[String(d.destination_id)] = d.timezone || 'UTC';
    });
    return m;
  }, [destinations]);

  const sunByDateDest = useMemo(() => {
    const m = {};
    (Array.isArray(sunEvents) ? sunEvents : []).forEach(item => {
      const key = `${item.date}_${item.destination_id}`;
      const list = Array.isArray(item.events) ? item.events : [];
      m[key] = list;
    });
    return m;
  }, [sunEvents]);

  const lodgingsByDestinationId = useMemo(() => {
    const m = {};
    (Array.isArray(lodgings) ? lodgings : []).forEach(l => {
      const id = l?.destination_id;
      if (!id) return;
      const key = String(id);
      if (!m[key]) m[key] = [];
      m[key].push(l);
    });
    Object.keys(m).forEach(k => {
      m[k].sort((a, b) => String(a.start_date || '').localeCompare(String(b.start_date || '')));
    });
    return m;
  }, [lodgings]);

  const { lodgingTypeOptions, lodgingCategoryId } = useMemo(() => {
    const lodgingCat = (Array.isArray(activityCategories) ? activityCategories : []).find(c => String(c?.category_name || '').toLowerCase() === 'lodging');
    const lodgingCatId = lodgingCat ? String(lodgingCat.category_id) : '';
    if (!lodgingCatId) return { lodgingTypeOptions: [], lodgingCategoryId: '' };
    const options = (Array.isArray(activityTypes) ? activityTypes : [])
      .filter(t => String(t?.category_id) === lodgingCatId)
      .slice()
      .sort((a, b) => String(a?.type_name || '').localeCompare(String(b?.type_name || '')))
      .map(t => String(t?.type_name || '').trim())
      .filter(Boolean);
    return { lodgingTypeOptions: options, lodgingCategoryId: lodgingCatId };
  }, [activityCategories, activityTypes]);

  const lodgingEventsByDateDest = useMemo(() => buildLodgingEventsByDateDest(lodgings), [lodgings]);

  useEffect(() => {
    if (!currentDestinationId && sortedDestinations.length) {
      setCurrentDestinationId(sortedDestinations[0].destination_id);
    }
  }, [currentDestinationId, sortedDestinations]);

  useEffect(() => {
    if (!sortedDestinations.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Prefer the most-visible destination section.
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => (b.intersectionRatio - a.intersectionRatio));
        const top = visible[0];
        const id = top?.target?.getAttribute('data-destination-id');
        if (id) setCurrentDestinationId(prev => (prev === id ? prev : id));
      },
      {
        root: null,
        // Make "current" whichever section is near the center of the viewport.
        rootMargin: '-40% 0px -55% 0px',
        threshold: [0.05, 0.15, 0.3, 0.5, 0.75],
      }
    );

    sortedDestinations.forEach(d => {
      const el = destinationSectionRefs.current[d.destination_id];
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [sortedDestinations]);

  useEffect(() => {
    if (!currentDestinationId) return;
    const tab = destinationTabRefs.current[currentDestinationId];
    if (tab && typeof tab.scrollIntoView === 'function') {
      try {
        tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      } catch {
        // ignore
      }
    }
  }, [currentDestinationId]);

  // =========================
  // CRUD: activities
  // =========================
  const addActivity = (date, destinationId) => {
    const key = `${date}_${destinationId}`;
    const act = newActivity[key] || {};
    const missing = [];
    if (!act.title) missing.push('Title');
    if (missing.length) {
      window.alert(`Please provide:\n- ${missing.join('\n- ')}`);
      return;
    }

    const useLegs = !!act.use_legs;
    const draftLegs = Array.isArray(act.legs) ? act.legs : [];

    const legsPayload = buildLegsPayload({
      useLegs,
      legsDraft: draftLegs,
      typeId: act.type_id,
      fallbackDate: date,
    });

    if (useLegs && (!legsPayload || !legsPayload.length)) {
      window.alert('Please add at least one leg with From/To.');
      return;
    }

    const { startIso, endIso } = deriveStartEnd({
      useLegs,
      legsPayload,
      dateStr: date,
      startTime: act.start_time,
      endTime: act.end_time,
      endDateStr: useLegs ? undefined : (act.end_date || date),
    });

    const from_address = String(act.from_address || '').trim();
    const to_address = String(act.to_address || '').trim();
    const location_address = String(act.location_address || '').trim();
    const location_lat = Number.isFinite(Number(act.location_lat)) ? Number(act.location_lat) : null;
    const location_lon = Number.isFinite(Number(act.location_lon)) ? Number(act.location_lon) : null;
    const location_city = String(act.location_city || '').trim() || (location_address ? cityKeyFromText(location_address) : null);
    const booking_url = String(act.booking_url || '').trim();
    const booking_ref = String(act.booking_ref || '').trim();

    fetch(apiUrl(`/api/trips/${tripId}/activities`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: act.title,
        description: act.description || '',
        destination_id: destinationId,
        type_id: act.type_id || null,
        location_address: location_address || null,
        location_lat,
        location_lon,
        location_city: location_city || null,
        booking_url: booking_url || null,
        booking_ref: booking_ref || null,
        start_time: startIso,
        end_time: endIso,
        ...(useLegs ? { legs: legsPayload } : {
          airline: String(act.airline || '').trim() || null,
          ...(from_address ? {
            from_address,
            from_lat: Number.isFinite(Number(act.from_lat)) ? Number(act.from_lat) : null,
            from_lon: Number.isFinite(Number(act.from_lon)) ? Number(act.from_lon) : null,
            from_city: String(act.from_city || '').trim() || null,
          } : {}),
          ...(to_address ? {
            to_address,
            to_lat: Number.isFinite(Number(act.to_lat)) ? Number(act.to_lat) : null,
            to_lon: Number.isFinite(Number(act.to_lon)) ? Number(act.to_lon) : null,
            to_city: String(act.to_city || '').trim() || null,
          } : {}),
        }),
      })
    })
      .then(async (res) => {
        const payload = await res.json().catch(() => ({}));
        if (!res.ok) {
          window.alert(payload.error || 'Failed to add activity');
          throw new Error(payload.error || 'Failed to add activity');
        }
        return payload;
      })
      .then((payload) => {
        setNewActivity(prev => ({ ...prev, [key]: {} }));
        setOpenAddActivityKey(null);
        if (payload?.unesco_suggestion) {
          setUnescoSuggestion({ activityId: payload.activity_id, suggestion: payload.unesco_suggestion });
        }
        fetch(apiUrl(`/api/trips/${tripId}/activities`))
          .then(res => res.json())
          .then(setActivities);
      });
  };

  const handleFlightImport = useCallback(async (flights) => {
    setShowFlightImport(false);
    let created = 0, updated = 0, failed = 0;

    for (const flight of flights) {
      // Strip internal dedup metadata before sending to the API
      const { _status, _existingActivityId, _existingTitle, _existingDescription, ...payload } = flight;

      try {
        let res;
        if (_status === 'update' && _existingActivityId) {
          // Preserve the user's existing title and description; only update times + legs
          res = await fetch(apiUrl(`/api/trips/${tripId}/activities/${_existingActivityId}`), {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ...payload,
              // Keep existing title/description rather than overwriting with parsed values
              title: _existingTitle || payload.title,
              description: payload.description || _existingDescription || '',
            }),
          });
          if (res.ok) updated++;
          else { const e = await res.json().catch(() => ({})); console.error('Flight update failed:', e, flight); failed++; }
        } else {
          res = await fetch(apiUrl(`/api/trips/${tripId}/activities`), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          if (res.ok) created++;
          else { const e = await res.json().catch(() => ({})); console.error('Flight import failed:', e, flight); failed++; }
        }
      } catch (e) {
        console.error('Flight import error:', e, flight);
        failed++;
      }
    }

    if (failed > 0 && created + updated === 0) {
      window.alert('Flights could not be imported. Check the browser console for details.');
    }

    fetch(apiUrl(`/api/trips/${tripId}/activities`))
      .then(res => res.json())
      .then(setActivities);
    fetchRoutePointsForTrip();
  }, [tripId, fetchRoutePointsForTrip]);

  const openEditActivityModal = (activity, date, destinationId) => {
    const key = `${date}_${destinationId}`;
    const type = activityTypes.find(t => String(t.type_id) === String(activity.type_id));
    const categoryId = type ? String(type.category_id) : '';
    const destTz = destinationTzById[String(destinationId)] || 'UTC';
    const legsDrafts = hydrateLegDraftsFromActivity(activity, date);
    const endDate = activity.end_time ? formatDateLocal(activity.end_time, destTz).slice(0, 10) : String(date || '').slice(0, 10);

    setNewActivity(prev => ({
      ...prev,
      [key]: {
        title: activity.title || '',
        description: activity.description || '',
        category_id: categoryId,
        type_id: String(activity.type_id || ''),
        start_time: formatTime24(activity.start_time, destTz) || '',
        end_time: formatTime24(activity.end_time, destTz) || '',
        end_date: endDate,
        location_address: activity.location_address || '',
        location_lat: activity.location_lat ?? null,
        location_lon: activity.location_lon ?? null,
        location_city: activity.location_city || '',
        booking_url: activity.booking_url || '',
        booking_ref: activity.booking_ref || '',
        airline: activity.airline || '',
        from_address: activity.from_address || '',
        from_lat: activity.from_lat ?? null,
        from_lon: activity.from_lon ?? null,
        from_city: activity.from_city || '',
        to_address: activity.to_address || '',
        to_lat: activity.to_lat ?? null,
        to_lon: activity.to_lon ?? null,
        to_city: activity.to_city || '',
        use_legs: legsDrafts.length > 0,
        legs: legsDrafts,
        activity_id: activity.activity_id,
      }
    }));
    setOpenAddActivityKey(key);
  };

  const saveActivityFromModal = async (date, destinationId) => {
    const key = `${date}_${destinationId}`;
    const draft = newActivity[key] || {};
    const activityId = draft.activity_id;
    if (!activityId) return;

    const missing = [];
    if (!draft.title || !String(draft.title).trim()) missing.push('Title');
    if (missing.length) {
      window.alert(`Please provide:\n- ${missing.join('\n- ')}`);
      return;
    }

    const useLegs = !!draft.use_legs;
    const draftLegs = Array.isArray(draft.legs) ? draft.legs : [];

    const legsPayload = buildLegsPayload({
      useLegs,
      legsDraft: draftLegs,
      typeId: draft.type_id,
      fallbackDate: date,
    });

    if (useLegs && (!legsPayload || !legsPayload.length)) {
      window.alert('Please add at least one leg with From/To.');
      return;
    }

    const { startIso, endIso } = deriveStartEnd({
      useLegs,
      legsPayload,
      dateStr: date,
      startTime: draft.start_time,
      endTime: draft.end_time,
      endDateStr: useLegs ? undefined : (draft.end_date || date),
    });

    const location_address = String(draft.location_address || '').trim();
    const location_lat = Number.isFinite(Number(draft.location_lat)) ? Number(draft.location_lat) : null;
    const location_lon = Number.isFinite(Number(draft.location_lon)) ? Number(draft.location_lon) : null;
    const location_city = String(draft.location_city || '').trim() || (location_address ? cityKeyFromText(location_address) : null);
    const booking_url = String(draft.booking_url || '').trim();
    const booking_ref = String(draft.booking_ref || '').trim();

    const res = await fetch(apiUrl(`/api/trips/${tripId}/activities/${activityId}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: String(draft.title).trim(),
        description: draft.description || '',
        destination_id: destinationId,
        type_id: draft.type_id || null,
        location_address: location_address || null,
        location_lat,
        location_lon,
        location_city: location_city || null,
        booking_url: booking_url || null,
        booking_ref: booking_ref || null,
        start_time: startIso,
        end_time: endIso,
        ...(useLegs ? { legs: legsPayload } : {
          airline: String(draft.airline || '').trim() || null,
          from_address: String(draft.from_address || '').trim() || null,
          from_lat: Number.isFinite(Number(draft.from_lat)) ? Number(draft.from_lat) : null,
          from_lon: Number.isFinite(Number(draft.from_lon)) ? Number(draft.from_lon) : null,
          from_city: String(draft.from_city || '').trim() || null,
          to_address: String(draft.to_address || '').trim() || null,
          to_lat: Number.isFinite(Number(draft.to_lat)) ? Number(draft.to_lat) : null,
          to_lon: Number.isFinite(Number(draft.to_lon)) ? Number(draft.to_lon) : null,
          to_city: String(draft.to_city || '').trim() || null,
        }),
      })
    });

    const payload = await res.json().catch(() => ({}));
    if (!res.ok) {
      window.alert(payload.error || 'Failed to update activity');
      return;
    }

    setNewActivity(prev => ({ ...prev, [key]: {} }));
    setOpenAddActivityKey(null);
    fetch(apiUrl(`/api/trips/${tripId}/activities`))
      .then(r => r.json())
      .then(setActivities);
  };

  const addLodging = async (destinationId) => {
    const key = String(destinationId);
    const draft = newLodgingByDestinationId[key] || {};
    const files = newLodgingFilesByDestinationId[key] || [];

    const lodging_name = String(draft.lodging_name || '').trim();
    const lodging_type = String(draft.lodging_type || '').trim();
    const address = String(draft.address || '').trim();
    const lat = Number.isFinite(Number(draft.lat)) ? Number(draft.lat) : null;
    const lon = Number.isFinite(Number(draft.lon)) ? Number(draft.lon) : null;
    const start_date = draft.start_date || '';
    const end_date = draft.end_date || '';
    const check_in_time = String(draft.check_in_time || '').trim();
    const check_out_time = String(draft.check_out_time || '').trim();
    const booking_url = String(draft.booking_url || '').trim();
    const booking_image_url = String(draft.booking_image_url || '').trim();

    if (!requireFields([
      [lodging_name, 'Name'], [lodging_type, 'Type'], [address, 'Address'],
      [start_date, 'Start Date'], [end_date, 'End Date'],
      [check_in_time, 'Check-in Time'], [check_out_time, 'Check-out Time'],
    ])) return;
    if (!validateDateRange(start_date, end_date)) return;

    const res = await fetch(apiUrl(`/api/trips/${tripId}/lodgings`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        destination_id: destinationId,
        lodging_name,
        lodging_type,
        address,
        lat,
        lon,
        start_date,
        end_date,
        check_in_time,
        check_out_time,
        booking_url: booking_url || null,
        booking_image_url: booking_image_url || null,
      })
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) {
      window.alert(payload.error || 'Failed to add lodging');
      return;
    }

    const lodgingId = payload?.lodging_id;
    setNewLodgingByDestinationId(prev => ({ ...prev, [key]: {} }));
    setNewLodgingFilesByDestinationId(prev => ({ ...prev, [key]: [] }));
    fetch(apiUrl(`/api/trips/${tripId}/lodgings`))
      .then(r => r.ok ? r.json() : [])
      .then(setLodgings)
      .catch(() => { /* ignore */ });

    if (lodgingId && Array.isArray(files) && files.length) {
      for (const f of files) {
        await uploadLodgingImage(lodgingId, f);
      }
      fetch(apiUrl(`/api/trips/${tripId}/lodgings`))
        .then(r => r.ok ? r.json() : [])
        .then(setLodgings)
        .catch(() => { /* ignore */ });
    }
  };

  const startEditingLodging = (lodging) => {
    setEditingLodgingId(lodging?.lodging_id || null);
    setEditLodging({
      lodging_name: lodging?.lodging_name || '',
      lodging_type: lodging?.lodging_type || '',
      address: lodging?.address || '',
      lat: lodging?.lat ?? null,
      lon: lodging?.lon ?? null,
      start_date: lodging?.start_date || '',
      end_date: lodging?.end_date || '',
      check_in_time: lodging?.check_in_time || '',
      check_out_time: lodging?.check_out_time || '',
      booking_url: lodging?.booking_url || '',
      booking_image_url: lodging?.booking_image_url || '',
    });
    setEditLodgingFiles([]);
  };

  const cancelEditingLodging = () => {
    setEditingLodgingId(null);
    setEditLodging(createEmptyLodgingDraft());
    setEditLodgingFiles([]);
  };

  const saveLodging = async (lodgingId) => {
    const lodging_name = String(editLodging.lodging_name || '').trim();
    const lodging_type = String(editLodging.lodging_type || '').trim();
    const address = String(editLodging.address || '').trim();
    const lat = Number.isFinite(Number(editLodging.lat)) ? Number(editLodging.lat) : null;
    const lon = Number.isFinite(Number(editLodging.lon)) ? Number(editLodging.lon) : null;
    const start_date = editLodging.start_date || '';
    const end_date = editLodging.end_date || '';
    const check_in_time = String(editLodging.check_in_time || '').trim();
    const check_out_time = String(editLodging.check_out_time || '').trim();
    const booking_url = String(editLodging.booking_url || '').trim();
    const booking_image_url = String(editLodging.booking_image_url || '').trim();

    if (!requireFields([
      [lodging_name, 'Name'], [lodging_type, 'Type'], [address, 'Address'],
      [start_date, 'Start Date'], [end_date, 'End Date'],
      [check_in_time, 'Check-in Time'], [check_out_time, 'Check-out Time'],
    ])) return;
    if (!validateDateRange(start_date, end_date)) return;

    const res = await fetch(apiUrl(`/api/trips/${tripId}/lodgings/${lodgingId}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        lodging_name,
        lodging_type,
        address,
        lat,
        lon,
        start_date,
        end_date,
        check_in_time,
        check_out_time,
        booking_url: booking_url || null,
        booking_image_url: booking_image_url || null,
      })
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) {
      window.alert(payload.error || 'Failed to update lodging');
      return;
    }

    if (Array.isArray(editLodgingFiles) && editLodgingFiles.length) {
      for (const f of editLodgingFiles) {
        await uploadLodgingImage(lodgingId, f);
      }
      setEditLodgingFiles([]);
    }
    cancelEditingLodging();
    fetch(apiUrl(`/api/trips/${tripId}/lodgings`))
      .then(r => r.ok ? r.json() : [])
      .then(setLodgings)
      .catch(() => { /* ignore */ });
  };

  const removeLodging = async (lodgingId) => {
    const ok = window.confirm('Delete this lodging?');
    if (!ok) return;

    const res = await fetch(apiUrl(`/api/trips/${tripId}/lodgings/${lodgingId}`), {
      method: 'DELETE'
    });
    if (!res.ok && res.status !== 204) {
      const payload = await res.json().catch(() => ({}));
      window.alert(payload.error || 'Failed to delete lodging');
      return;
    }

    if (String(editingLodgingId) === String(lodgingId)) cancelEditingLodging();
    setLodgings(prev => (Array.isArray(prev) ? prev.filter(l => String(l?.lodging_id) !== String(lodgingId)) : prev));
  };

  const removeActivity = (activityId) => {
    fetch(apiUrl(`/api/trips/${tripId}/activities/${activityId}`), {
      method: 'DELETE'
    })
      .then(async (res) => {
        if (!res.ok) {
          const payload = await res.json().catch(() => ({}));
          window.alert(payload.error || 'Failed to remove activity');
          throw new Error(payload.error || 'Failed to remove activity');
        }
      })
      .then(() => {
        setActivities(acts => acts.filter(a => a.activity_id !== activityId));
        setEditingByActivityId(prev => {
          const next = { ...prev };
          delete next[activityId];
          return next;
        });
      });
  };

  // =========================
  // CRUD: activities (edit)
  // =========================
  const cancelEditing = (activityId) => {
    setEditingByActivityId(prev => {
      const next = { ...prev };
      delete next[activityId];
      return next;
    });
  };

  const saveActivity = async (activityId, destinationId) => {
    const edit = editingByActivityId[activityId];
    if (!edit) return;

    const missing = [];
    if (!edit.title || !String(edit.title).trim()) missing.push('Title');
    if (missing.length) {
      window.alert(`Please provide:\n- ${missing.join('\n- ')}`);
      return;
    }

    const date = edit.date;
    const useLegs = !!edit.use_legs;
    const editLegs = Array.isArray(edit.legs) ? edit.legs : [];

    const legsPayload = buildLegsPayload({
      useLegs,
      legsDraft: editLegs,
      typeId: edit.type_id,
      fallbackDate: date,
    });

    if (useLegs && (!legsPayload || !legsPayload.length)) {
      window.alert('Please add at least one leg with From/To.');
      return;
    }

    const { startIso, endIso } = deriveStartEnd({
      useLegs,
      legsPayload,
      dateStr: date,
      startTime: edit.start_time,
      endTime: edit.end_time,
      endDateStr: useLegs ? undefined : (edit.end_date || date),
    });

    const location_address = String(edit.location_address || '').trim();
    const location_lat = Number.isFinite(Number(edit.location_lat)) ? Number(edit.location_lat) : null;
    const location_lon = Number.isFinite(Number(edit.location_lon)) ? Number(edit.location_lon) : null;
    const location_city = String(edit.location_city || '').trim() || (location_address ? cityKeyFromText(location_address) : null);
    const booking_url = String(edit.booking_url || '').trim();
    const booking_ref = String(edit.booking_ref || '').trim();

    const res = await fetch(apiUrl(`/api/trips/${tripId}/activities/${activityId}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: String(edit.title).trim(),
        description: edit.description || '',
        destination_id: destinationId,
        type_id: edit.type_id || null,
        location_address: location_address || null,
        location_lat,
        location_lon,
        location_city: location_city || null,
        booking_url: booking_url || null,
        booking_ref: booking_ref || null,
        start_time: startIso,
        end_time: endIso,
        ...(useLegs ? { legs: legsPayload } : {
          from_address: String(edit.from_address || '').trim() || null,
          from_lat: Number.isFinite(Number(edit.from_lat)) ? Number(edit.from_lat) : null,
          from_lon: Number.isFinite(Number(edit.from_lon)) ? Number(edit.from_lon) : null,
          from_city: String(edit.from_city || '').trim() || null,
          to_address: String(edit.to_address || '').trim() || null,
          to_lat: Number.isFinite(Number(edit.to_lat)) ? Number(edit.to_lat) : null,
          to_lon: Number.isFinite(Number(edit.to_lon)) ? Number(edit.to_lon) : null,
          to_city: String(edit.to_city || '').trim() || null,
        }),
      })
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) {
      window.alert(payload.error || 'Failed to update activity');
      return;
    }

    cancelEditing(activityId);
    fetch(apiUrl(`/api/trips/${tripId}/activities`))
      .then(r => r.json())
      .then(setActivities);
  };

  // =========================
  // CRUD: destinations
  // =========================
  const addDestination = () => {
    const missing = [];
    if (!newDestination.destination_name) missing.push('Destination Name');
    if (!newDestination.start_date) missing.push('Start Date');
    if (!newDestination.end_date) missing.push('End Date');
    if (missing.length) {
      window.alert(`Please provide:\n- ${missing.join('\n- ')}`);
      return;
    }

    if (newDestination.start_date && newDestination.end_date && newDestination.end_date < newDestination.start_date) {
      window.alert('End Date must be on or after Start Date.');
      return;
    }
    const payload = {
      ...newDestination,
      place: selectedPlace || undefined,
    };

    const anyLodging = Object.values(newDestinationLodging || {}).some(v => String(v || '').trim());
    if (anyLodging) {
      const missingL = [];
      if (!String(newDestinationLodging.lodging_name || '').trim()) missingL.push('Lodging Name');
      if (!String(newDestinationLodging.lodging_type || '').trim()) missingL.push('Lodging Type');
      if (!String(newDestinationLodging.address || '').trim()) missingL.push('Address');
      if (!newDestinationLodging.start_date) missingL.push('Lodging Start Date');
      if (!newDestinationLodging.end_date) missingL.push('Lodging End Date');
      if (!String(newDestinationLodging.check_in_time || '').trim()) missingL.push('Check-in Time');
      if (!String(newDestinationLodging.check_out_time || '').trim()) missingL.push('Check-out Time');
      if (missingL.length) {
        window.alert(`Please provide:\n- ${missingL.join('\n- ')}`);
        return;
      }
      if (newDestinationLodging.end_date < newDestinationLodging.start_date) {
        window.alert('Lodging End Date must be on or after Start Date.');
        return;
      }

      payload.lodging = {
        lodging_name: String(newDestinationLodging.lodging_name || '').trim(),
        lodging_type: String(newDestinationLodging.lodging_type || '').trim(),
        address: String(newDestinationLodging.address || '').trim(),
        lat: Number.isFinite(Number(newDestinationLodging.lat)) ? Number(newDestinationLodging.lat) : null,
        lon: Number.isFinite(Number(newDestinationLodging.lon)) ? Number(newDestinationLodging.lon) : null,
        start_date: newDestinationLodging.start_date,
        end_date: newDestinationLodging.end_date,
        check_in_time: String(newDestinationLodging.check_in_time || '').trim(),
        check_out_time: String(newDestinationLodging.check_out_time || '').trim(),
        booking_url: String(newDestinationLodging.booking_url || '').trim() || null,
        booking_image_url: String(newDestinationLodging.booking_image_url || '').trim() || null,
      };
    }

    fetch(apiUrl(`/api/trips/${tripId}/destinations`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(res => res.json())
      .then(() => {
        setNewDestination({ destination_name: '', start_date: '', end_date: '' });
        setNewDestinationLodging({ lodging_name: '', lodging_type: '', address: '', start_date: '', end_date: '', check_in_time: '', check_out_time: '', booking_url: '', booking_image_url: '' });
        setIsAddingDestination(false);
        setIsAddingDestinationLodging(false);
        setSelectedPlace(null);
        setDestinationSuggestions([]);
        setActiveSuggestionIndex(-1);
        fetch(apiUrl(`/api/trips/${tripId}/destinations`))
          .then(res => res.json())
          .then(setDestinations);

        // Ensure itinerary route map updates immediately after adding.
        fetchRoutePointsForTrip();

        fetch(apiUrl(`/api/trips/${tripId}/lodgings`))
          .then(r => r.ok ? r.json() : [])
          .then(setLodgings)
          .catch(() => { /* ignore */ });
      });
  };

  const startEditingDestination = (destination) => {
    setEditingDestinationId(destination.destination_id);
    setCollapsedDestinationIds(prev => ({
      ...prev,
      [String(destination.destination_id)]: false,
    }));
    setEditDestination({
      destination_name: destination.destination_name || '',
      start_date: destination.start_date || '',
      end_date: destination.end_date || '',
    });
    setEditSelectedPlace(null);
    setEditDestinationSuggestions([]);
    setEditActiveSuggestionIndex(-1);
  };

  const cancelEditingDestination = () => {
    setEditingDestinationId(null);
    setEditSelectedPlace(null);
    setEditDestinationSuggestions([]);
    setEditActiveSuggestionIndex(-1);
  };

  const saveDestination = async (destinationId) => {
    const missing = [];
    if (!editDestination.destination_name) missing.push('Destination Name');
    if (!editDestination.start_date) missing.push('Start Date');
    if (!editDestination.end_date) missing.push('End Date');
    if (missing.length) {
      window.alert(`Please provide:\n- ${missing.join('\n- ')}`);
      return;
    }

    if (editDestination.start_date && editDestination.end_date && editDestination.end_date < editDestination.start_date) {
      window.alert('End Date must be on or after Start Date.');
      return;
    }

    const payload = {
      ...editDestination,
      place: editSelectedPlace || undefined,
    };

    const res = await fetch(apiUrl(`/api/trips/${tripId}/destinations/${destinationId}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    const out = await res.json().catch(() => ({}));
    if (!res.ok) {
      window.alert(out.error || 'Failed to update destination');
      return;
    }

    cancelEditingDestination();
    fetch(apiUrl(`/api/trips/${tripId}/destinations`))
      .then(r => r.json())
      .then(setDestinations);

    // Ensure itinerary route map updates immediately after editing.
    fetchRoutePointsForTrip();
  };

  const removeDestination = (destinationId) => {
    fetch(apiUrl(`/api/trips/${tripId}/destinations/${destinationId}`), {
      method: 'DELETE'
    })
      .then(() => {
        setDestinations(dests => (Array.isArray(dests) ? dests.filter(d => String(d.destination_id) !== String(destinationId)) : []));
        fetchRoutePointsForTrip();
      });
  };

  const activityEntriesByDateDest = useMemo(() => {
    const out = {};
    const seenByKey = {};

    const typeNameById = (typeId) => {
      const t = (Array.isArray(activityTypes) ? activityTypes : []).find(x => String(x.type_id) === String(typeId));
      return t ? String(t.type_name || '') : '';
    };

    const markPrimaryForKey = (key, activityId) => {
      const k = String(key || '');
      const id = String(activityId || '');
      if (!k || !id) return false;
      if (!seenByKey[k]) seenByKey[k] = new Set();
      if (seenByKey[k].has(id)) return false;
      seenByKey[k].add(id);
      return true;
    };

    const dayOffset = (fromDateStr, toDateStr) => {
      const a = String(fromDateStr || '').slice(0, 10);
      const b = String(toDateStr || '').slice(0, 10);
      if (!a || !b) return 0;
      try {
        const da = new Date(`${a}T00:00:00Z`);
        const db = new Date(`${b}T00:00:00Z`);
        if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return 0;
        return Math.round((db.getTime() - da.getTime()) / 86400000);
      } catch {
        return 0;
      }
    };

    const clampDateToDestination = (dateStr, destId) => {
      const d = (Array.isArray(destinations) ? destinations : []).find(x => String(x?.destination_id) === String(destId));
      if (!d) return dateStr;
      const s = String(d.start_date || '').slice(0, 10);
      const e = String(d.end_date || '').slice(0, 10);
      if (!s || !e || !dateStr) return dateStr;
      if (dateStr < s) return s;
      if (dateStr > e) return e;
      return dateStr;
    };

    // Travel legs to/from a place that isn't a trip stop (e.g. the flight home)
    // have no destination_id. Attach each to the destination active on its
    // departure date so it still shows on the itinerary — in time order within
    // that day — instead of being dropped from the destination view.
    const transitDestinationId = (act) => {
      const depDate = String(
        act?.legs?.[0]?.depart_local_date ||
        (act?.start_time ? String(act.start_time).slice(0, 10) : '') ||
        ''
      ).slice(0, 10);
      if (!depDate) return null;
      // Outbound/connecting legs: the destination they arrive into (first starting on/after).
      let target = sortedDestinations.find(d => String(d.start_date || '').slice(0, 10) >= depDate);
      // Return/home legs: nothing follows, so the destination being departed (last starting on/before).
      if (!target) {
        for (const d of sortedDestinations) {
          if (String(d.start_date || '').slice(0, 10) <= depDate) target = d;
        }
      }
      return target ? target.destination_id : null;
    };

    (Array.isArray(activities) ? activities : []).forEach((act) => {
      let destId = act?.destination_id;
      if (!destId) {
        // Only travel legs get an inferred destination; non-travel orphans stay out.
        if (!isTravelTypeId(act?.type_id)) return;
        destId = transitDestinationId(act);
        if (!destId) return;
      }
      const destKey = String(destId);
      const destTz = destinationTzById[destKey] || 'UTC';

      const legs = Array.isArray(act?.legs) ? act.legs : [];
      if (legs.length) {
        const sortedLegs = [...legs].sort((a, b) => (Number(a?.leg_index) || 0) - (Number(b?.leg_index) || 0));

        sortedLegs.forEach((leg, idx) => {
          const depTz = leg?.from_tz || destTz;
          const rowDate = String(
            leg?.depart_local_date ||
            formatDateLocal(leg?.depart_time || act?.start_time, depTz) ||
            ''
          ).slice(0, 10);
          if (!rowDate) return;
          const key = `${rowDate}_${destKey}`;
          if (!out[key]) out[key] = [];

          const arrTz = leg?.to_tz || destTz;
          const depDate = String(leg?.depart_local_date || formatDateLocal(leg?.depart_time, depTz) || '').slice(0, 10);
          const arrDate = String(leg?.arrive_local_date || formatDateLocal(leg?.arrive_time, arrTz) || '').slice(0, 10);

          out[key].push({
            row_kind: 'leg',
            row_id: `leg-${act.activity_id}-${idx}-${rowDate}`,
            row_date: rowDate,
            end_date: arrDate,
            activity: act,
            primary_for_day: markPrimaryForKey(key, act.activity_id),
            leg_index: Number(leg?.leg_index) || idx,
            start_local: formatTime24(leg?.depart_time || act?.start_time, depTz),
            end_local: formatTime24(leg?.arrive_time || act?.end_time, arrTz),
            end_day_offset: dayOffset(depDate, arrDate),
          });

          // Overnight leg: also surface the landing on the arrival day.
          if (arrDate && depDate && arrDate !== depDate) {
            const arrClamped = clampDateToDestination(arrDate, destId);
            if (arrClamped) {
              const arrKey = `${arrClamped}_${destKey}`;
              if (!out[arrKey]) out[arrKey] = [];
              out[arrKey].push({
                row_kind: 'arrival',
                row_id: `arrival-${act.activity_id}-${idx}-${arrClamped}`,
                row_date: arrClamped,
                end_date: arrClamped,
                activity: act,
                leg_index: Number(leg?.leg_index) || idx,
                primary_for_day: false,
                start_local: formatTime24(leg?.arrive_time, arrTz),
                end_local: '',
              });
            }
          }

          const nextLeg = sortedLegs[idx + 1];
          if (nextLeg && leg?.arrive_time && nextLeg?.depart_time) {
            const arriveTz = leg?.to_tz || destTz;
            const departTz = nextLeg?.from_tz || destTz;
            const arriveDate = String(
              leg?.arrive_local_date ||
              formatDateLocal(leg?.arrive_time, arriveTz) ||
              rowDate
            ).slice(0, 10);
            const departDate = String(
              nextLeg?.depart_local_date ||
              formatDateLocal(nextLeg?.depart_time, departTz) ||
              arriveDate
            ).slice(0, 10);
            const transferKey = `${arriveDate}_${destKey}`;
            if (!out[transferKey]) out[transferKey] = [];
            const aType = String(leg?.type_name || typeNameById(leg?.type_id) || '').trim().toLowerCase();
            const bType = String(nextLeg?.type_name || typeNameById(nextLeg?.type_id) || '').trim().toLowerCase();
            const title = (aType === 'flight' && bType === 'flight') ? 'Layover' : 'Connection';

            out[transferKey].push({
              row_kind: 'transfer',
              row_id: `transfer-${act.activity_id}-${idx}-${arriveDate}`,
              row_date: arriveDate,
              end_date: departDate,
              activity: act,
              primary_for_day: false,
              title,
              start_local: formatTime24(leg?.arrive_time, arriveTz),
              end_local: formatTime24(nextLeg?.depart_time, departTz),
            });
          }
        });

        return;
      }

      if (!act.start_time) return;
      const dateStr = formatDateLocal(act.start_time, destTz);
      if (!dateStr) return;
      const endDateStr = act.end_time ? formatDateLocal(act.end_time, destTz) : '';
      const offset = endDateStr ? dayOffset(dateStr, endDateStr) : 0;
      const key = `${dateStr}_${destKey}`;
      if (!out[key]) out[key] = [];
      out[key].push({
        row_kind: 'activity',
        row_id: `act-${act.activity_id}-${dateStr}`,
        row_date: dateStr,
        end_date: endDateStr,
        activity: act,
        primary_for_day: markPrimaryForKey(key, act.activity_id),
        start_local: formatTime24(act.start_time, destTz),
        end_local: formatTime24(act.end_time, destTz),
        end_day_offset: offset,
      });

      // Overnight activity: also show arrival on the end date
      if (offset > 0 && endDateStr) {
        const arrDateClamped = clampDateToDestination(endDateStr, destId);
        if (!arrDateClamped) return;
        const arrKey = `${arrDateClamped}_${destKey}`;
        if (!out[arrKey]) out[arrKey] = [];
        out[arrKey].push({
          row_kind: 'arrival',
          row_id: `arrival-${act.activity_id}-${arrDateClamped}`,
          row_date: arrDateClamped,
          end_date: arrDateClamped,
          activity: act,
          primary_for_day: false,
          start_local: formatTime24(act.end_time, destTz),
          end_local: '',
        });
      }
    });

    return out;
  }, [activities, destinations, destinationTzById, activityTypes, sortedDestinations, isTravelTypeId]);

  // Trip-level transit activities: travel type with no destination (moves between cities)
  const tripTransitActivities = useMemo(() => {
    return (Array.isArray(activities) ? activities : []).filter(
      act => !act.destination_id && isTravelTypeId(act.type_id)
    );
  }, [activities, isTravelTypeId]);

  // Map each trip-level transit to the destination it precedes (based on departure date)
  const transitBeforeDestination = useMemo(() => {
    const byDest = {};
    const orphans = [];

    const sorted = [...tripTransitActivities].sort((a, b) => {
      const ad = String(a.legs?.[0]?.depart_local_date || a.start_time || '').slice(0, 10);
      const bd = String(b.legs?.[0]?.depart_local_date || b.start_time || '').slice(0, 10);
      return ad.localeCompare(bd);
    });

    for (const act of sorted) {
      const depDate = String(
        act.legs?.[0]?.depart_local_date ||
        (act.start_time ? act.start_time.slice(0, 10) : '') ||
        ''
      ).slice(0, 10);

      if (!depDate) { orphans.push(act); continue; }

      // Outbound/connecting legs: attach to the destination they precede.
      let target = sortedDestinations.find(d =>
        String(d.start_date || '').slice(0, 10) >= depDate
      );

      // Return/home legs depart after the last destination has started, so
      // nothing "follows" them — attach to the destination active on that date
      // (the last one starting on/before it, i.e. the one being departed from).
      if (!target) {
        for (const d of sortedDestinations) {
          if (String(d.start_date || '').slice(0, 10) <= depDate) target = d;
        }
      }

      if (target) {
        const key = String(target.destination_id);
        byDest[key] = [...(byDest[key] || []), act];
      } else {
        orphans.push(act);
      }
    }

    return { byDest, orphans };
  }, [tripTransitActivities, sortedDestinations]);

  const renderTransitCard = (act) => {
    const typeName = String(
      activityTypes.find(t => String(t.type_id) === String(act.type_id))?.type_name || ''
    ).toLowerCase();
    const icon = typeName === 'flight' ? '✈'
      : typeName.includes('ferry') || typeName.includes('boat') || typeName.includes('cruise') ? '⛴'
      : typeName.includes('train') || typeName.includes('rail') ? '🚆'
      : typeName.includes('bus') || typeName.includes('coach') ? '🚌'
      : '→';

    const legs = Array.isArray(act.legs) && act.legs.length ? act.legs : null;
    const firstLeg = legs?.[0];
    const lastLeg = legs?.[legs.length - 1];
    const fromDisplay = firstLeg?.from_address || firstLeg?.from_city || act.from_address || act.from_city || '';
    const toDisplay = lastLeg?.to_address || lastLeg?.to_city || act.to_address || act.to_city || '';
    const depDate = firstLeg?.depart_local_date || String(act.start_time || '').slice(0, 10) || '';
    const depTime = firstLeg?.depart_time
      ? formatTime24(firstLeg.depart_time, firstLeg.from_tz || 'UTC')
      : (act.start_time ? formatTime24(act.start_time, 'UTC') : '');

    return (
      <div
        key={act.activity_id}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '10px 16px',
          marginBottom: 12,
          background: 'rgba(255,255,255,0.025)',
          border: '1px dashed rgba(255,255,255,0.15)',
          borderRadius: 10,
          fontSize: 13,
        }}
      >
        <span style={{ fontSize: 15, minWidth: 20, textAlign: 'center', opacity: 0.8 }}>{icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontWeight: 500 }}>{act.title}</span>
          {fromDisplay && toDisplay && (
            <span style={{ color: 'var(--tp-muted)', marginLeft: 8 }}>
              {fromDisplay} → {toDisplay}
            </span>
          )}
          {(depDate || depTime) && (
            <span style={{ color: 'var(--tp-muted)', marginLeft: 8, fontSize: 12 }}>
              {[depDate, depTime].filter(Boolean).join(' · ')}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
          <Link
            to={`/itinerary/${tripId}/activity/${act.activity_id}`}
            style={{ fontSize: 12, color: 'var(--tp-muted)', textDecoration: 'none' }}
          >
            Edit
          </Link>
          <button
            type="button"
            className="tp-btn--ghost"
            style={{ fontSize: 13, padding: '1px 6px', color: 'var(--tp-danger, #e55)', lineHeight: 1 }}
            onClick={() => {
              if (window.confirm(`Delete "${act.title || 'this transit'}"?`)) removeActivity(act.activity_id);
            }}
          >
            ×
          </button>
        </div>
      </div>
    );
  };


  return (
    <div>
      {/* ========================= */}
      {/* Container: Hero header     */}
      {/* ========================= */}
      <section className="tp-hero tp-hero--compact">
        <div className="tp-row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'nowrap', gap: 16 }}>
          <div>
            <p className="tp-hero__kicker">Itinerary</p>
            <h1 className="tp-hero__title">{tripName || 'Trip'}</h1>
            <div className="tp-row" style={{ gap: 14 }}>
              <a
                className="tp-link"
                href={apiUrl(`/api/trips/${tripId}/calendar.ics`)}
                style={{ fontSize: 13 }}
              >
                ⬇ Export calendar (.ics)
              </a>
              <a
                className="tp-link"
                href={apiUrl(`/api/trips/${tripId}/itinerary.pdf`)}
                style={{ fontSize: 13 }}
              >
                ⬇ Export PDF
              </a>
            </div>
          </div>
          <label className="tp-switch" title="Mark this trip as completed" style={{ marginTop: 6, flexShrink: 0 }}>
            <input
              type="checkbox"
              checked={tripCompleted}
              disabled={isSavingTripCompleted}
              onChange={async (e) => {
                const next = Boolean(e.target.checked);
                const prev = tripCompleted;
                setTripCompleted(next);
                setIsSavingTripCompleted(true);
                try {
                  const resp = await fetch(apiUrl(`/api/trips/${tripId}/completed`), {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ completed: next })
                  });
                  if (!resp.ok) throw new Error('Failed to update trip');
                } catch {
                  setTripCompleted(prev);
                } finally {
                  setIsSavingTripCompleted(false);
                }
              }}
            />
            <span className="tp-switch__track" aria-hidden="true">
              <span className="tp-switch__thumb" aria-hidden="true" />
            </span>
            <span className="tp-switch__label">{tripCompleted ? 'Completed' : 'Planning'}</span>
          </label>
        </div>

        <div className="tp-row" style={{ marginTop: 14, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div className="tp-row" style={{ gap: 6 }}>
            {VIEW_MODES.map((mode) => (
              <button
                key={mode}
                type="button"
                className={viewMode === mode ? '' : 'tp-btn--ghost'}
                onClick={() => setViewMode(mode)}
              >
                {VIEW_MODE_LABELS[mode] || mode}
              </button>
            ))}
          </div>
          <PageActions
            actions={[
              { label: 'Research', to: `/trips/${tripId}/research`, variant: 'ghost' },
              { label: 'Inspiration', to: `/trips/${tripId}/inspiration`, variant: 'ghost' },
              { label: 'Edit Destinations', to: `/trip-setup/${tripId}?from=${encodeURIComponent(`/itinerary/${tripId}`)}`, variant: 'solid' },
            ]}
          />
        </div>
      </section>

      {/* ========================= */}
      {/* Day View: one day at a time */}
      {/* ========================= */}
      {viewMode === 'day' && (
        <section className="tp-section">
          <ItineraryDayView embedded />
        </section>
      )}

      {viewMode === 'travel' && (
        <TravelOverview
          tripId={tripId}
          activities={activities}
          activityTypes={activityTypes}
          isTravelTypeId={isTravelTypeId}
          onDelete={removeActivity}
        />
      )}

      {viewMode === 'calendar' && (
        <TripCalendar
          tripId={tripId}
          activities={activities}
          lodgings={lodgings}
          destinations={destinations}
        />
      )}

      {/* ========================= */}
      {/* Destination & Overview: shared container */}
      {/* Container: Destinations    */}
      {/* + Route map + Day tables   */}
      {/* ========================= */}
      {(viewMode === 'destination' || viewMode === 'overview') && (
      <section className="tp-section">
        <div className={`tp-card ${isAddingDestination ? '' : 'tp-card--tight'}`}>
          <div className="tp-row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 0 }}>
            <h2 className="tp-section__title" style={{ margin: 0 }}>Destinations</h2>
            {effectiveMapFitPoints.length > 0 && (
              <button
                type="button"
                className="tp-btn--ghost"
                style={{ fontSize: 12, padding: '4px 10px' }}
                onClick={() => setMapVisible(v => !v)}
              >
                {mapVisible ? '▲ Hide map' : '▼ Show map'}
              </button>
            )}
          </div>

          {!isAddingDestination && (
            <div className="tp-card tp-card--tight" style={{ marginTop: 12 }}>
              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  overflowX: 'auto',
                  padding: 6,
                  scrollBehavior: 'smooth',
                  WebkitOverflowScrolling: 'touch'
                }}
              >
                {sortedDestinations.map(d => {
                  const active = String(currentDestinationId) === String(d.destination_id);
                  return (
                    <button
                      key={d.destination_id}
                      type="button"
                      ref={(el) => {
                        if (el) destinationTabRefs.current[d.destination_id] = el;
                      }}
                      className={active ? '' : 'tp-btn--ghost'}
                      onClick={() => {
                        setCurrentDestinationId(d.destination_id);
                        const target = destinationSectionRefs.current[d.destination_id];
                        if (target && typeof target.scrollIntoView === 'function') {
                          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }}
                    >
                      {d.destination_name}
                    </button>
                  );
                })}
                <button
                  type="button"
                  className="tp-btn--icon"
                  aria-label="Add destination"
                  onClick={() => setIsAddingDestination(true)}
                  style={{ flexShrink: 0 }}
                >
                  +
                </button>
                <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
                  <button
                    type="button"
                    className="tp-btn--ghost"
                    style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}
                    onClick={() => setShowFlightImport(true)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.8 19.2L16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/>
                    </svg>
                    Import Flights
                  </button>
                </div>
              </div>
            </div>
          )}

          {isAddingDestination && (
            <div style={{ marginTop: 12 }}>
              <h3 className="tp-section__title" style={{ margin: '0 0 12px', fontSize: 14 }}>Add Destination</h3>

              <div className="tp-grid">
            <div className="tp-col-6">
              <div className="tp-field">
                <span className="tp-label">Destination Name</span>
                <input
                  type="text"
                  placeholder="e.g., Amsterdam"
                  required
                  value={newDestination.destination_name}
                  onChange={e => {
                    setNewDestination({ ...newDestination, destination_name: e.target.value });
                    setSelectedPlace(null);
                  }}
                  onKeyDown={e => {
                    if (!destinationSuggestions.length) return;
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setActiveSuggestionIndex(i => (i + 1) % destinationSuggestions.length);
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setActiveSuggestionIndex(i => (i <= 0 ? destinationSuggestions.length - 1 : i - 1));
                    } else if (e.key === 'Enter') {
                      e.preventDefault();
                      const idx = activeSuggestionIndex >= 0 ? activeSuggestionIndex : 0;
                      selectSuggestion(destinationSuggestions[idx]);
                    } else if (e.key === 'Escape') {
                      setDestinationSuggestions([]);
                      setActiveSuggestionIndex(-1);
                    }
                  }}
                />
                {destinationSuggestions.length > 0 && (
                  <div className="tp-card tp-card--tight" style={{ marginTop: 10 }}>
                    {destinationSuggestions.map((s, idx) => (
                      <div
                        key={`${s.osm_type || 'osm'}-${s.osm_id || idx}`}
                        onMouseDown={() => selectSuggestion(s)}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          borderTop: idx === 0 ? 'none' : '1px solid var(--tp-border)',
                          background: idx === activeSuggestionIndex ? 'var(--tp-surface-2)' : 'transparent'
                        }}
                      >
                        <div style={{ fontSize: 14 }}>{s.label || s.display_name}</div>
                        {!!s.subtitle && (
                          <div style={{ fontSize: 12, color: 'var(--tp-muted)', marginTop: 2 }}>{s.subtitle}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="tp-col-6">
              <div className="tp-grid" style={{ gap: 12 }}>
                <div className="tp-col-6">
                  <div className="tp-field">
                    <span className="tp-label">Start Date</span>
                    <input
                      type="date"
                      required
                      value={newDestination.start_date}
                      onClick={openNativePicker}
                      onChange={e => {
                        const v = e.target.value;
                        setNewDestination(prev => ({
                          ...prev,
                          start_date: v,
                          end_date: prev.end_date && v && prev.end_date < v ? '' : prev.end_date,
                        }));
                      }}
                    />
                  </div>
                </div>
                <div className="tp-col-6">
                  <div className="tp-field">
                    <span className="tp-label">End Date</span>
                    <input
                      type="date"
                      required
                      min={newDestination.start_date || ''}
                      value={newDestination.end_date}
                      onClick={openNativePicker}
                      onChange={e => {
                        const v = e.target.value;
                        setNewDestination(prev => {
                          const start = prev.start_date;
                          if (start && v && v < start) return prev;
                          return { ...prev, end_date: v };
                        });
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {!isAddingDestinationLodging ? (
            <div className="tp-row" style={{ marginTop: 12, justifyContent: 'space-between' }}>
              <span className="tp-pill">Lodging (optional)</span>
              <button className="tp-btn--ghost" onClick={() => setIsAddingDestinationLodging(true)}>Add Lodging</button>
            </div>
          ) : (
            <div className="tp-card tp-card--tight" style={{ marginTop: 12 }}>
              <div className="tp-row" style={{ justifyContent: 'space-between' }}>
                <h3 className="tp-section__title" style={{ margin: 0, fontSize: 14 }}>Lodging</h3>
                <button className="tp-btn--ghost" onClick={() => {
                  setIsAddingDestinationLodging(false);
                  setNewDestinationLodging(createEmptyLodgingDraft());
                }}>Cancel</button>
              </div>
              <LodgingForm
                draft={newDestinationLodging}
                setDraft={setNewDestinationLodging}
                lodgingTypeOptions={lodgingTypeOptions}
                activityTypes={activityTypes}
                lodgingCategoryId={lodgingCategoryId}
                onTypeCreated={refetchTypes}
                openNativePicker={openNativePicker}
                showSubmit={false}
              />
            </div>
          )}

          <div className="tp-row" style={{ gap: 8 }}>
            <button onClick={addDestination}>Add Destination</button>
            <button
              type="button"
              className="tp-btn--ghost"
              onClick={() => {
                setIsAddingDestination(false);
                setIsAddingDestinationLodging(false);
                setNewDestination({ destination_name: '', start_date: '', end_date: '' });
                setNewDestinationLodging({ lodging_name: '', lodging_type: '', address: '', start_date: '', end_date: '', check_in_time: '', check_out_time: '', booking_url: '', booking_image_url: '' });
                setSelectedPlace(null);
                setDestinationSuggestions([]);
                setActiveSuggestionIndex(-1);
              }}
            >
              Cancel
            </button>
          </div>
            </div>
          )}
        </div>

        {/* Container: Route map */}
        {effectiveMapFitPoints.length > 0 && mapVisible && (
          <div
            className="tp-map tp-map--dark"
            style={{ marginTop: 12, height: 360, borderRadius: 16, overflow: 'hidden' }}
          >
            <TripMap
              center={effectiveMapFitPoints.length === 1 ? effectiveMapFitPoints[0] : [20, 0]}
              zoom={effectiveMapFitPoints.length === 1 ? (isDestinationCityView ? 12 : 5) : 3}
              fitPoints={effectiveMapFitPoints}
              fitKey={`route-${viewMode}-${currentDestinationId}-${effectiveMapFitPoints.length}-${mapCountryCode}`}
              fitMaxZoom={mapFitMaxZoom}
              countryCode={mapCountryCode}
              theme="dark"
              height={360}
              markers={[
                ...(displayRouteStops || []).map((s, idx) => ({
                  lat: s.lat,
                  lon: s.lon,
                  title: s.destination_name,
                  popup: `Stop ${idx + 1}`,
                  iconUrl: markerIconDataUrl(s.kind === 'travel' ? 'Travel' : ''),
                  radius: 5,
                })),
                ...activityMapPoints.map((p) => ({
                  lat: p.lat,
                  lon: p.lon,
                  title: p.title,
                  popup: p.title,
                  address: p.address,
                  iconUrl: p.iconUrl,
                  color: p.color,
                  radius: 4,
                })),
              ]}
              polylines={mapPolylines}
            />
          </div>
        )}
        {destinations.length === 0 && (
          <p style={{ color: 'var(--tp-muted)', marginTop: 0 }}>No destinations added yet.</p>
        )}

        {destinationsToShow.map(destination => {
          const destinationIdStr = String(destination.destination_id);
          const isEditing = String(editingDestinationId) === destinationIdStr;
          const isCollapsed = forceExpanded ? false : (collapsedDestinationIds[destinationIdStr] ?? true);
          const showBody = isEditing || !isCollapsed || forceExpanded;

          // Overview lists travel between destinations as standalone cards.
          // When a destination is expanded its day-grid already shows the leg
          // (activityEntriesByDateDest now folds in destination-less travel), so
          // only show the card while collapsed — avoids a duplicate. The
          // destination view (always expanded) shows the leg in-grid instead.
          const transitsBeforeThis = (viewMode === 'overview' && !showBody)
            ? (transitBeforeDestination.byDest[String(destination.destination_id)] || [])
            : [];

          return (
          <React.Fragment key={destination.destination_id}>
            {transitsBeforeThis.map(act => renderTransitCard(act))}
            {/* Container: Destination card */}
            <div
              className="tp-card"
              style={{ marginBottom: 16 }}
              data-destination-id={destination.destination_id}
              ref={(el) => {
                if (el) destinationSectionRefs.current[destination.destination_id] = el;
              }}
            >
            <div className="tp-row" style={{ justifyContent: 'space-between' }}>
              {isEditing ? (
                <div style={{ flex: 1 }}>
                  <div className="tp-field" style={{ marginBottom: 0 }}>
                    <input
                      type="text"
                      value={editDestination.destination_name}
                      onChange={e => {
                        setEditDestination(prev => ({ ...prev, destination_name: e.target.value }));
                        setEditSelectedPlace(null);
                      }}
                      onKeyDown={e => {
                        if (!editDestinationSuggestions.length) return;
                        if (e.key === 'ArrowDown') {
                          e.preventDefault();
                          setEditActiveSuggestionIndex(i => (i + 1) % editDestinationSuggestions.length);
                        } else if (e.key === 'ArrowUp') {
                          e.preventDefault();
                          setEditActiveSuggestionIndex(i => (i <= 0 ? editDestinationSuggestions.length - 1 : i - 1));
                        } else if (e.key === 'Enter') {
                          e.preventDefault();
                          const idx = editActiveSuggestionIndex >= 0 ? editActiveSuggestionIndex : 0;
                          selectEditSuggestion(editDestinationSuggestions[idx]);
                        } else if (e.key === 'Escape') {
                          setEditDestinationSuggestions([]);
                          setEditActiveSuggestionIndex(-1);
                        }
                      }}
                    />
                    {editDestinationSuggestions.length > 0 && (
                      <div className="tp-card tp-card--tight" style={{ marginTop: 10 }}>
                        {editDestinationSuggestions.map((s, idx) => (
                          <div
                            key={`${s.osm_type || 'osm'}-${s.osm_id || idx}`}
                            onMouseDown={() => selectEditSuggestion(s)}
                            style={{
                              padding: '10px 12px',
                              cursor: 'pointer',
                              borderTop: idx === 0 ? 'none' : '1px solid var(--tp-border)',
                              background: idx === editActiveSuggestionIndex ? 'var(--tp-surface-2)' : 'transparent'
                            }}
                          >
                            <div style={{ fontSize: 14 }}>{s.label || s.display_name}</div>
                            {!!s.subtitle && (
                              <div style={{ fontSize: 12, color: 'var(--tp-muted)', marginTop: 2 }}>{s.subtitle}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <h2 className="tp-section__title" style={{ margin: 0 }}>{destination.destination_name}</h2>
              )}

              {isEditing ? (
                <div className="tp-row">
                  <button onClick={() => saveDestination(destination.destination_id)}>Save</button>
                  <button className="tp-btn--ghost" onClick={cancelEditingDestination}>Cancel</button>
                </div>
              ) : (
                <div className="tp-row">
                  {!forceExpanded && (
                    <button
                      type="button"
                      className="tp-btn--ghost"
                      onClick={() => setCollapsedDestinationIds(prev => ({
                        ...prev,
                        [destinationIdStr]: !prev[destinationIdStr],
                      }))}
                    >
                      {isCollapsed ? 'Expand' : 'Collapse'}
                    </button>
                  )}

                  <div style={{ position: 'relative', display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      className="tp-btn--icon"
                      aria-label="Destination actions"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenDestinationMenuId((cur) => (String(cur) === String(destination.destination_id) ? null : destination.destination_id));
                      }}
                    >
                      ⋮
                    </button>
                    {String(openDestinationMenuId) === String(destination.destination_id) && (
                      <div
                        className="tp-card tp-card--tight tp-menu"
                        style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 60, minWidth: 180 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          className="tp-btn--ghost"
                          style={{ width: '100%', textAlign: 'left' }}
                          onClick={() => {
                            setOpenDestinationMenuId(null);
                            startEditingDestination(destination);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="tp-btn--danger"
                          style={{ width: '100%', textAlign: 'left', marginTop: 8 }}
                          onClick={() => {
                            setOpenDestinationMenuId(null);
                            const ok = window.confirm(`Remove destination "${destination.destination_name}"? This will remove it from the trip.`);
                            if (!ok) return;
                            removeDestination(destination.destination_id);
                          }}
                        >
                          Remove Destination
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {showBody && (
              <>
                {isEditing && (
                  <div className="tp-grid" style={{ marginTop: 12, gap: 12 }}>
                    <div className="tp-col-6">
                      <div className="tp-field">
                        <span className="tp-label">Start Date</span>
                        <input
                          type="date"
                          value={editDestination.start_date}
                          onClick={openNativePicker}
                          onChange={e => {
                            const v = e.target.value;
                            setEditDestination(prev => ({
                              ...prev,
                              start_date: v,
                              end_date: prev.end_date && v && prev.end_date < v ? '' : prev.end_date,
                            }));
                          }}
                        />
                      </div>
                    </div>
                    <div className="tp-col-6">
                      <div className="tp-field">
                        <span className="tp-label">End Date</span>
                        <input
                          type="date"
                          min={editDestination.start_date || ''}
                          value={editDestination.end_date}
                          onClick={openNativePicker}
                          onChange={e => {
                            const v = e.target.value;
                            setEditDestination(prev => {
                              const start = prev.start_date;
                              if (start && v && v < start) return prev;
                              return { ...prev, end_date: v };
                            });
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="tp-card tp-card--tight" style={{ marginTop: 12 }}>
                  {/* Container: Lodging list */}
                  <div className="tp-row" style={{ justifyContent: 'space-between' }}>
                    <h3 className="tp-section__title" style={{ margin: 0, fontSize: 14 }}>Lodging</h3>
                    <span className="tp-pill">{(lodgingsByDestinationId[String(destination.destination_id)] || []).length} item(s)</span>
                  </div>

                  {(lodgingsByDestinationId[String(destination.destination_id)] || []).length === 0 && (
                    <p style={{ margin: '10px 0 0', color: 'var(--tp-muted)' }}>No lodging added yet.</p>
                  )}

                  {(lodgingsByDestinationId[String(destination.destination_id)] || []).map(l => (
                <div key={l.lodging_id} id={`lodging-${l.lodging_id}`} style={{ marginTop: 10 }}>
                  {String(editingLodgingId) === String(l.lodging_id) ? (
                    <div>
                      <div className="tp-row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
                        <span className="tp-pill">Edit lodging</span>
                        <div className="tp-row" style={{ gap: 8 }}>
                          <button className="tp-btn--ghost" onClick={cancelEditingLodging}>Cancel</button>
                          <button onClick={() => saveLodging(l.lodging_id)}>Save</button>
                        </div>
                      </div>
                      <LodgingForm
                        draft={editLodging}
                        setDraft={setEditLodging}
                        lodgingTypeOptions={lodgingTypeOptions}
                        activityTypes={activityTypes}
                        lodgingCategoryId={lodgingCategoryId}
                        onTypeCreated={refetchTypes}
                        openNativePicker={openNativePicker}
                        showSubmit={false}
                      />
                      <div className="tp-field" style={{ marginTop: 8 }}>
                        <span className="tp-label">Upload Images</span>
                        <input type="file" accept="image/*" multiple onChange={(e) => setEditLodgingFiles(Array.from(e.target.files || []))} />
                        {editLodgingFiles.length ? <div style={{ fontSize: 12, color: 'var(--tp-muted)', marginTop: 4 }}>{editLodgingFiles.length} file(s) selected</div> : null}
                      </div>
                    </div>
                  ) : (
                    <div className="tp-lodging-card">
                      <div className="tp-lodging-card__info">
                        <div style={{ display: 'grid', gap: 6 }}>
                          <div style={{ fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {String(l?.booking_url || '').trim() ? (
                              <a
                                href={l.booking_url}
                                target="_blank"
                                rel="noreferrer"
                                style={{ color: 'inherit', textDecoration: 'underline' }}
                              >
                                {l.lodging_name}
                              </a>
                            ) : (
                              l.lodging_name
                            )}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--tp-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {l.lodging_type}
                            {String(l?.address || '').trim() ? (
                              <>
                                {' • '}
                                <a
                                  href={buildGoogleMapsUrl(l.address)}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{ color: 'inherit', textDecoration: 'underline' }}
                                  title={l.address}
                                >
                                  {formatAddressSummary(l.address)}
                                </a>
                              </>
                            ) : null}
                          </div>
                        </div>

                        <div className="tp-lodging-card__meta">
                          <span className="tp-pill">{formatDateReadable(l.start_date)} → {formatDateReadable(l.end_date)}</span>
                          <div style={{ position: 'relative', display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              className="tp-btn--icon"
                              aria-label="Lodging actions"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenLodgingMenuId((cur) => (String(cur) === String(l.lodging_id) ? null : l.lodging_id));
                              }}
                            >
                              ⋮
                            </button>
                            {String(openLodgingMenuId) === String(l.lodging_id) && (
                              <div
                                className="tp-card tp-card--tight tp-menu"
                                style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 60, minWidth: 140 }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  type="button"
                                  className="tp-btn--ghost"
                                  style={{ width: '100%', textAlign: 'left' }}
                                  onClick={() => {
                                    setOpenLodgingMenuId(null);
                                    startEditingLodging(l);
                                  }}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="tp-btn--ghost"
                                  style={{ width: '100%', textAlign: 'left', marginTop: 4 }}
                                  onClick={() => {
                                    setOpenLodgingMenuId(null);
                                    setManagingPhotosLodging(l);
                                  }}
                                >
                                  Manage Photos
                                </button>
                                <button
                                  type="button"
                                  className="tp-btn--danger"
                                  style={{ width: '100%', textAlign: 'left', marginTop: 8 }}
                                  onClick={() => {
                                    setOpenLodgingMenuId(null);
                                    removeLodging(l.lodging_id);
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {(() => {
                        const url = String(l?.booking_url || '').trim();
                        const prev = url ? lodgingPreviewByUrl[url] : null;
                        const previewImg = String(prev?.image || '').trim();
                        const fallbackImg = String(l?.booking_image_url || '').trim();
                        const mediaList = Array.isArray(l?.images)
                          ? l.images.map((img) => String(img?.media_url || '').trim()).filter(Boolean)
                          : [];
                        const allImages = mediaList.length ? mediaList : [previewImg || fallbackImg].filter(Boolean);
                        if (!allImages.length) return null;
                        const idx = Math.max(0, (lodgingCarouselIndex[l.lodging_id] ?? 0) % allImages.length);
                        const current = toAbsoluteImageUrl(allImages[idx]);
                        if (!current) return null;
                        const title = String(prev?.title || '').trim() || l.lodging_name || url;
                        return (
                          <div className="tp-lodging-card__media">
                            <button
                              type="button"
                              className="tp-btn--ghost"
                              onClick={() => setLodgingCarouselIndex(prev => ({
                                ...prev,
                                [l.lodging_id]: (idx - 1 + allImages.length) % allImages.length,
                              }))}
                              disabled={allImages.length <= 1}
                            >
                              ‹
                            </button>
                            <img
                              src={current}
                              alt={title || ''}
                              className="tp-lodging-card__img"
                              loading="lazy"
                            />
                            <button
                              type="button"
                              className="tp-btn--ghost"
                              onClick={() => setLodgingCarouselIndex(prev => ({
                                ...prev,
                                [l.lodging_id]: (idx + 1) % allImages.length,
                              }))}
                              disabled={allImages.length <= 1}
                            >
                              ›
                            </button>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              ))}

              {!isAddingLodgingByDestinationId[String(destination.destination_id)] ? (
                <div className="tp-row" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
                  <button className="tp-btn--ghost" onClick={() => setIsAddingLodgingByDestinationId(prev => ({ ...prev, [String(destination.destination_id)]: true }))}>Add Lodging</button>
                </div>
              ) : (
                <div>
                  <div className="tp-row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
                    <span className="tp-pill">New lodging</span>
                    <button
                      className="tp-btn--ghost"
                      onClick={() => {
                        setIsAddingLodgingByDestinationId(prev => ({ ...prev, [String(destination.destination_id)]: false }));
                        setNewLodgingByDestinationId(prev => ({ ...prev, [String(destination.destination_id)]: {} }));
                        setNewLodgingFilesByDestinationId(prev => ({ ...prev, [String(destination.destination_id)]: [] }));
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                  {(() => {
                    const destKey = String(destination.destination_id);
                    const draft = newLodgingByDestinationId[destKey] || {};
                    const setDraft = (updater) => setNewLodgingByDestinationId(prev => {
                      const curr = prev[destKey] || {};
                      const next = typeof updater === 'function' ? updater(curr) : updater;
                      return { ...prev, [destKey]: next };
                    });
                    const files = newLodgingFilesByDestinationId[destKey] || [];
                    return (
                      <>
                        <LodgingForm
                          draft={draft}
                          setDraft={setDraft}
                          lodgingTypeOptions={lodgingTypeOptions}
                          activityTypes={activityTypes}
                          lodgingCategoryId={lodgingCategoryId}
                          onTypeCreated={refetchTypes}
                          openNativePicker={openNativePicker}
                          showSubmit={false}
                        />
                        <div className="tp-field" style={{ marginTop: 8 }}>
                          <span className="tp-label">Upload Images</span>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={(e) => setNewLodgingFilesByDestinationId(prev => ({ ...prev, [destKey]: Array.from(e.target.files || []) }))}
                          />
                          {files.length ? <div style={{ fontSize: 12, color: 'var(--tp-muted)', marginTop: 4 }}>{files.length} file(s) selected</div> : null}
                        </div>
                        <div style={{ marginTop: 8 }}>
                          <button onClick={() => addLodging(destination.destination_id)}>Add Lodging</button>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>

            {getDatesForDestination(destination).map(date => {
              const key = `${date}_${destination.destination_id}`;
              const sunList = sunByDateDest[key] || [];
              const lodgingList = lodgingEventsByDateDest[key] || [];
              const combined = [];

              (Array.isArray(sunList) ? sunList : []).forEach((e, idx) => {
                const t = e?.time;
                combined.push({
                  row_kind: 'sun',
                  row_id: `sun-${idx}-${e?.kind || 'event'}-${t || ''}`,
                  title: e?.label || 'Sun event',
                  start_local: t || '',
                });
              });

              (Array.isArray(lodgingList) ? lodgingList : []).forEach((e) => {
                combined.push(e);
              });

              (activityEntriesByDateDest[key] || []).forEach((entry) => {
                combined.push(entry);
              });

              combined.sort((a, b) => {
                const am = toMinutes(a.start_local);
                const bm = toMinutes(b.start_local);
                if (am == null && bm == null) return 0;
                if (am == null) return 1;
                if (bm == null) return -1;
                return am - bm;
              });

              return (
                <div key={key} className="tp-card tp-card--tight" style={{ marginTop: 12 }}>
                  {/* Container: Day section (sun + activities) */}
                  <div className="tp-row" style={{ justifyContent: 'space-between' }}>
                    <span className="tp-pill">{date}</span>
                    <span className="tp-pill">Activities: {new Set((activityEntriesByDateDest[key] || []).map(e => String(e?.activity?.activity_id))).size}</span>
                  </div>

                  {(() => {
                    const dayIds = Array.from(new Set((activityEntriesByDateDest[key] || [])
                      .filter(e => e && e.activity && e.activity.activity_id)
                      .map(e => String(e.activity.activity_id))));
                    const selected = Array.isArray(selectedActivityIdsByKey[key]) ? selectedActivityIdsByKey[key] : [];
                    const selectedSet = new Set(selected.map(String));
                    const allSelected = dayIds.length > 0 && dayIds.every(id => selectedSet.has(id));

                    const setAll = (checked) => {
                      setSelectedActivityIdsByKey(prev => ({
                        ...prev,
                        [key]: checked ? dayIds : []
                      }));
                    };

                    const bulkDelete = async () => {
                      const ids = (Array.isArray(selectedActivityIdsByKey[key]) ? selectedActivityIdsByKey[key] : []).map(String);
                      if (!ids.length) return;
                      const ok = window.confirm(`Delete ${ids.length} selected activit${ids.length === 1 ? 'y' : 'ies'}?`);
                      if (!ok) return;

                      await Promise.all(ids.map((activityId) =>
                        fetch(apiUrl(`/api/trips/${tripId}/activities/${activityId}`), { method: 'DELETE' })
                      ));

                      setSelectedActivityIdsByKey(prev => ({ ...prev, [key]: [] }));
                      fetch(apiUrl(`/api/trips/${tripId}/activities`))
                        .then(r => r.ok ? r.json() : [])
                        .then(setActivities)
                        .catch(() => { /* ignore */ });
                    };

                    return (
                      <div className="tp-row" style={{ marginTop: 10, justifyContent: 'space-between', alignItems: 'center' }}>
                        <label className="tp-row" style={{ gap: 8, color: 'var(--tp-muted)' }}>
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={(e) => setAll(e.target.checked)}
                          />
                          <span style={{ fontSize: 12 }}>Select all</span>
                        </label>
                        <div className="tp-row" style={{ alignItems: 'center' }}>
                          <span className="tp-pill">Selected: {selectedSet.size}</span>
                          <div style={{ position: 'relative', display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              className="tp-btn--icon"
                              aria-label="Bulk actions"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenBulkMenuKey((cur) => (String(cur) === String(key) ? null : key));
                              }}
                            >
                              ⋮
                            </button>
                            {String(openBulkMenuKey) === String(key) && (
                              <div
                                className="tp-card tp-card--tight tp-menu"
                                style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 60, minWidth: 170 }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  type="button"
                                  className="tp-btn--danger"
                                  style={{ width: '100%', textAlign: 'left' }}
                                  disabled={selectedSet.size === 0}
                                  onClick={() => {
                                    setOpenBulkMenuKey(null);
                                    bulkDelete();
                                  }}
                                >
                                  Delete selected
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  <div style={{ marginTop: 10 }}>
                    {/* Container: Activities table */}
                    <table className="tp-table">
                      <thead>
                        <tr>
                          <th className="tp-th--tight" aria-label="Select"></th>
                          <th>Title</th>
                          <th>Start</th>
                          <th>End</th>
                          <th>Description</th>
                          <th>Category</th>
                          <th>Type</th>
                          <th className="tp-th--tight" aria-label="Actions"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {combined.map(row => {
                          if (row.row_kind === 'sun') {
                            const muted = { color: 'var(--tp-muted)' };
                            const mutedStrike = { color: 'var(--tp-muted)', textDecoration: 'line-through' };
                            return (
                              <tr key={row.row_id}>
                                <td className="tp-td--tight"></td>
                                <td style={muted}>{row.title}</td>
                                <td>{row.start_local}</td>
                                <td style={mutedStrike}>—</td>
                                <td style={mutedStrike}>—</td>
                                <td style={mutedStrike}>—</td>
                                <td style={mutedStrike}>—</td>
                                <td className="tp-td--tight"></td>
                              </tr>
                            );
                          }

                          if (row.row_kind === 'lodging') {
                            const muted = { color: 'var(--tp-muted)' };
                            const mutedStrike = { color: 'var(--tp-muted)', textDecoration: 'line-through' };
                            const lodgingAnchor = row?.lodging_id ? `#lodging-${row.lodging_id}` : null;
                            return (
                              <tr key={row.row_id}>
                                <td className="tp-td--tight"></td>
                                <td style={muted}>
                                  {lodgingAnchor ? (
                                    <a href={lodgingAnchor} style={{ color: 'inherit', textDecoration: 'underline' }}>
                                      {row.title}
                                    </a>
                                  ) : (
                                    row.title
                                  )}
                                </td>
                                <td>{row.start_local || ''}</td>
                                <td style={mutedStrike}>—</td>
                                <td style={mutedStrike}>—</td>
                                <td style={mutedStrike}>—</td>
                                <td style={mutedStrike}>—</td>
                                <td className="tp-td--tight"></td>
                              </tr>
                            );
                          }

                          if (row.row_kind === 'transfer') {
                            const muted = { color: 'var(--tp-muted)' };
                            const mutedStrike = { color: 'var(--tp-muted)', textDecoration: 'line-through' };
                            return (
                              <tr key={row.row_id}>
                                <td className="tp-td--tight"></td>
                                <td style={muted}>{row.title || 'Layover'}</td>
                                <td>{row.start_local || ''}</td>
                                <td style={muted}>{row.end_local || '—'}</td>
                                <td style={mutedStrike}>—</td>
                                <td style={mutedStrike}>—</td>
                                <td style={mutedStrike}>—</td>
                                <td className="tp-td--tight"></td>
                              </tr>
                            );
                          }

                          if (row.row_kind === 'arrival') {
                            const act = row.activity || {};
                            const muted = { color: 'var(--tp-muted)' };
                            const mutedStrike = { color: 'var(--tp-muted)', textDecoration: 'line-through' };
                            const arrivalDesc = formatOvernightArrivalDescription(act);
                            return (
                              <tr key={row.row_id}>
                                <td className="tp-td--tight"></td>
                                <td style={muted}>
                                  <Link
                                    to={`/itinerary/${tripId}/activity/${act.activity_id}`}
                                    style={{ color: 'inherit', textDecoration: 'underline' }}
                                  >
                                    {act.title || 'Activity'}
                                    <span style={{ fontSize: 12, marginLeft: 4 }}>— arrives</span>
                                  </Link>
                                </td>
                                <td>{row.start_local || ''}</td>
                                <td style={mutedStrike}>—</td>
                                <td style={muted}>
                                  {arrivalDesc ? (
                                    <div style={{ fontSize: 12 }}>{arrivalDesc}</div>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                                <td style={mutedStrike}>—</td>
                                <td style={mutedStrike}>—</td>
                                <td className="tp-td--tight">
                                  <div style={{ position: 'relative', display: 'flex', justifyContent: 'flex-end' }}>
                                    <button
                                      type="button"
                                      className="tp-btn--icon"
                                      aria-label="Activity actions"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setOpenActivityMenuId((cur) => (String(cur) === String(act.activity_id) ? null : act.activity_id));
                                      }}
                                    >
                                      ⋮
                                    </button>
                                    {String(openActivityMenuId) === String(act.activity_id) && (
                                      <div
                                        className="tp-card tp-card--tight tp-menu"
                                        style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 50, minWidth: 140 }}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <button
                                          type="button"
                                          className="tp-btn--ghost"
                                          style={{ width: '100%', textAlign: 'left' }}
                                          onClick={() => {
                                            setOpenActivityMenuId(null);
                                            openEditActivityModal(act, date, destination.destination_id);
                                          }}
                                        >
                                          Edit
                                        </button>
                                        <button
                                          type="button"
                                          className="tp-btn--danger"
                                          style={{ width: '100%', textAlign: 'left', marginTop: 8 }}
                                          onClick={() => {
                                            setOpenActivityMenuId(null);
                                            removeActivity(act.activity_id);
                                          }}
                                        >
                                          Delete
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          }

                          return (
                            <ActivityTableRow
                              key={row.row_id}
                              row={row}
                              date={date}
                              dayKey={key}
                              tripId={tripId}
                              destinationId={destination.destination_id}
                              activityTypes={activityTypes}
                              activityCategories={activityCategories}
                              isTravelTypeId={isTravelTypeId}
                              isTravelCategoryId={isTravelCategoryId}
                              editingByActivityId={editingByActivityId}
                              setEditingByActivityId={setEditingByActivityId}
                              selectedActivityIdsByKey={selectedActivityIdsByKey}
                              setSelectedActivityIdsByKey={setSelectedActivityIdsByKey}
                              openActivityMenuId={openActivityMenuId}
                              setOpenActivityMenuId={setOpenActivityMenuId}
                              openNativePicker={openNativePicker}
                              refetchTypes={refetchTypes}
                              saveActivity={saveActivity}
                              cancelEditing={cancelEditing}
                              removeActivity={removeActivity}
                              openEditActivityModal={openEditActivityModal}
                            />
                          );
                        })}

                        <tr>
                          <td colSpan={9}>
                            <div style={{ position: 'relative', display: 'inline-flex' }}>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenActivityMenuId(null);
                                  setOpenAddActivityKey((cur) => (String(cur) === String(key) ? null : key));
                                }}
                              >
                                Add activity
                              </button>

                              {String(openAddActivityKey) === String(key) && (
                                <div
                                  className="tp-card tp-card--tight"
                                  style={{
                                    position: 'fixed',
                                    left: '50%',
                                    top: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    zIndex: 1000,
                                    minWidth: 720,
                                    maxWidth: 'calc(100vw - 24px)',
                                    width: 720,
                                    maxHeight: 'calc(100vh - 24px)',
                                    overflowY: 'auto',
                                    background: 'var(--tp-bg)',
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <ActivityModal
                                    draft={newActivity[key] || {}}
                                    setDraft={(updater) => setNewActivity(prev => {
                                      const cur = prev[key] || {};
                                      const next = typeof updater === 'function' ? updater(cur) : updater;
                                      return { ...prev, [key]: next };
                                    })}
                                    date={date}
                                    activityCategories={activityCategories}
                                    activityTypes={activityTypes}
                                    refetchTypes={refetchTypes}
                                    openNativePicker={openNativePicker}
                                    cityKeyFromText={cityKeyFromText}
                                    onSubmit={() => {
                                      const d = newActivity[key] || {};
                                      if (d.activity_id) saveActivityFromModal(date, destination.destination_id);
                                      else addActivity(date, destination.destination_id);
                                    }}
                                    onCancel={() => {
                                      const d = newActivity[key] || {};
                                      if (d.activity_id) setNewActivity(prev => ({ ...prev, [key]: {} }));
                                      setOpenAddActivityKey(null);
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
              </>
            )}
          </div>
          </React.Fragment>
        );
      })}
      {viewMode === 'overview' && transitBeforeDestination.orphans.map(act => renderTransitCard(act))}
      </section>
      )}

      {showFlightImport && (
        <FlightImportModal
          onClose={() => setShowFlightImport(false)}
          onImport={handleFlightImport}
          destinations={sortedDestinations}
          activityTypes={activityTypes}
          activityCategories={activityCategories}
          existingActivities={activities}
        />
      )}

      {unescoSuggestion && (
        <UnescoSuggestionBanner
          suggestion={unescoSuggestion.suggestion}
          onConfirm={async () => {
            const labelsRes = await fetch(apiUrl('/api/labels'));
            const labels = await labelsRes.json().catch(() => []);
            const unescoLabel = labels.find(l => l.name === 'UNESCO World Heritage Site');
            if (unescoLabel) {
              const endpoint = unescoSuggestion.activityId
                ? apiUrl(`/api/activities/${unescoSuggestion.activityId}/labels`)
                : apiUrl(`/api/wishlist/${unescoSuggestion.ideaId}/labels`);
              await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ label_id: unescoLabel.label_id }),
              });
            }
            setUnescoSuggestion(null);
          }}
          onDismiss={() => setUnescoSuggestion(null)}
        />
      )}

      {managingPhotosLodging && (
        <LodgingImagesModal
          tripId={tripId}
          lodging={managingPhotosLodging}
          onClose={() => setManagingPhotosLodging(null)}
          onImagesChanged={(newImages) => {
            setLodgings(prev =>
              prev.map(l =>
                String(l.lodging_id) === String(managingPhotosLodging.lodging_id)
                  ? { ...l, images: newImages }
                  : l
              )
            );
            setManagingPhotosLodging(prev => prev ? { ...prev, images: newImages } : null);
          }}
        />
      )}
    </div>
  );
}

export default Itinerary;
